import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from 'api/api';

/**
 * useFetch(url)
 * Fetches `url` whenever it changes. Returns:
 *   { data, loading, error: { status, message } | null, retry }
 *
 * - `loading` is true during the first (and every retry) fetch.
 * - `retry` triggers a re-fetch of the same url.
 */
export function useFetch(url) {
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);
    const [tick, setTick]       = useState(0); // incremented by retry()

    useEffect(() => {
        if (!url) return;
        let cancelled = false;

        setLoading(true);
        setError(null);

        apiFetch({ url }).then(({ data: result, error: err }) => {
            if (cancelled) return;
            if (err) {
                setError(err);
                setData(null);
            } else {
                setData(result);
                setError(null);
            }
            setLoading(false);
        });

        return () => { cancelled = true; };
    }, [url, tick]); // re-runs when url changes or retry() is called

    const retry = useCallback(() => setTick(t => t + 1), []);

    return { data, loading, error, retry };
}
